import React, { useEffect, useState } from 'react'
import { InputGroup } from '@blueprintjs/core'
import cx from 'classnames'
import DownshiftUntyped, {
  DownshiftInterface,
  ControllerStateAndHelpers,
  GetItemPropsOptions,
} from 'downshift'
import {
  IAdminSearchEmployee,
  IAdminSearchDepartment,
  IAdminSearchSpace,
  IAdminSearchNeighborhood,
  IPageInfo,
} from '@api/types'
import { Flex, Icon } from '@ui'
import { KeyCodes } from '@constants'
import { UseAdminSearch } from './hooks/useAdminSearch'
import { MenuSeparator } from './AdminSearch'
import { useDrag } from 'react-dnd'
import CircleInfo from './assets/CircleInfo'
import useLocalStorage from 'use-local-storage'
import css from './employeeManagement.module.scss'
import { getEmptyImage } from 'react-dnd-html5-backend'
import api from '@api'
import { useFloor } from '@providers/mapProvider'

interface Props {
  search: UseAdminSearch
  onSelect: (value: IAdminSearchResultItem | null) => void
  renderItem: (item: IAdminSearchResultItem, helpers?: { closeMenu: () => void }) => void
  width?: string
  'data-input'?: string
  inputPlaceholder?: string
  queryTableMessage?: string
  leftIcon: React.ReactNode
  rightIcon: React.ReactNode
  setShowFilters: (showFilters: boolean) => void
  closeRightDrawers: () => void
}

type AdminSearchedDataType = {
  employee: {
    employees: IAdminSearchResultItem[]
    pageInfo: IPageInfo | undefined
  }
  department: {
    departments: IAdminSearchResultItem[]
    pageInfo: IPageInfo | undefined
  }
  neighborhood: {
    neighborhoods: IAdminSearchResultItem[]
    pageInfo: IPageInfo | undefined
  }
  space: {
    spaces: IAdminSearchResultItem[]
    pageInfo: IPageInfo | undefined
  }
}
export enum AdminSearchItemType {
  EMPLOYEE = 'employee',
  DEPARTMENT = 'department',
  NEIGHBORHOOD = 'neighborhood',
  SPACE = 'space',
}

export type IAdminSearchResultItems =
  | IAdminSearchEmployee
  | IAdminSearchDepartment
  | IAdminSearchSpace
  | IAdminSearchNeighborhood

export type IAdminSearchResultItem = IAdminSearchResultItems & { type: AdminSearchItemType }

const Downshift: DownshiftInterface<IAdminSearchResultItem> = DownshiftUntyped

export default function AdminSearchDropdown(props: Props) {
  const {
    search,
    onSelect,
    renderItem,
    width = '200px',
    inputPlaceholder = 'Search',
    leftIcon = <Icon name="search" />,
    rightIcon = <Icon name="close" />,
    setShowFilters,
    closeRightDrawers,
  } = props

  const initialState = {
    employee: {
      employees: [],
      pageInfo: { endCursor: '', hasNextPage: false },
    },
    department: {
      departments: [],
      pageInfo: { endCursor: '', hasNextPage: false },
    },
    neighborhood: {
      neighborhoods: [],
      pageInfo: { endCursor: '', hasNextPage: false },
    },
    space: {
      spaces: [],
      pageInfo: { endCursor: '', hasNextPage: false },
    },
  }
  const [localStorageItems, setLocalStorageItems] = useLocalStorage('admin-search', '')
  const [recentSearch, setRecentSearch] = useState<IAdminSearchResultItem[]>([])
  const [adminSearchedData, setAdminSearchedData] = useState<AdminSearchedDataType>(initialState)
  const { apId } = useFloor()
  useEffect(() => {
    if (!Boolean(search.query)) {
      getStoredItems()
    }

    async function getStoredItems() {
      try {
        const restoredItems = JSON.parse(localStorageItems)

        if (Boolean(restoredItems)) {
          setRecentSearch(restoredItems)
        }
      } catch (e) {
        console.log('Unable to unstore items')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.query])
  const minimumQueryLength = 2
  const hasRecentSearch = !Boolean(search.query) && Boolean(recentSearch.length)
  const sItems = search.items

  const employees =
    hasRecentSearch && recentSearch.some((i) => i.type === AdminSearchItemType.EMPLOYEE)
      ? recentSearch.filter((i) => i.type === AdminSearchItemType.EMPLOYEE)
      : sItems
      ? sItems.employees.edges.map((e) => ({ ...e.node, type: AdminSearchItemType.EMPLOYEE }))
      : []

  const departments =
    hasRecentSearch && recentSearch.some((i) => i.type === AdminSearchItemType.DEPARTMENT)
      ? recentSearch.filter((i) => i.type === AdminSearchItemType.DEPARTMENT)
      : sItems
      ? sItems.departments.edges.map((e) => ({
          ...e.node,
          type: AdminSearchItemType.DEPARTMENT,
        }))
      : []

  const neighborhoods =
    hasRecentSearch && recentSearch.some((i) => i.type === AdminSearchItemType.NEIGHBORHOOD)
      ? recentSearch.filter((i) => i.type === AdminSearchItemType.NEIGHBORHOOD)
      : sItems
      ? sItems.neighborhoods.edges.map((e) => ({
          ...e.node,
          type: AdminSearchItemType.NEIGHBORHOOD,
        }))
      : []

  const spaces =
    hasRecentSearch && recentSearch.some((i) => i.type === AdminSearchItemType.SPACE)
      ? recentSearch.filter((i) => i.type === AdminSearchItemType.SPACE)
      : sItems
      ? sItems.spaces.edges.map((e) => ({
          ...e.node,
          type: AdminSearchItemType.SPACE,
        }))
      : []

  const searchResultItems = [...employees, ...departments, ...neighborhoods, ...spaces]
  const searchedDate = {
    employee: { employees, pageInfo: sItems?.employees?.pageInfo },
    department: { departments, pageInfo: sItems?.departments?.pageInfo },
    neighborhood: { neighborhoods, pageInfo: sItems?.neighborhoods?.pageInfo },
    space: { spaces, pageInfo: sItems?.spaces?.pageInfo },
  }
  useEffect(() => {
    if (Boolean(searchResultItems.length)) {
      storeItems(searchResultItems)
    }

    async function storeItems(items: IAdminSearchResultItem[]) {
      try {
        const threeItems = items.splice(0, 3)

        setLocalStorageItems(JSON.stringify(threeItems))
      } catch (e) {
        console.log('Unable to store items')
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResultItems])

  useEffect(() => {
    setAdminSearchedData(searchedDate)
  }, [search.items])

  const showMoreItems = async (type: string) => {
    switch (type) {
      case AdminSearchItemType.EMPLOYEE:
        {
          const searchResults = await api.admin.adminEmployee({
            query: search.query,
            apId,
            after: adminSearchedData?.employee?.pageInfo?.endCursor,
          })
          const sortedSearchResults = searchResults?.edges.map((edge) => ({
            ...edge.node,
            type: type as AdminSearchItemType,
          })) as []

          setAdminSearchedData({
            ...adminSearchedData,
            employee: {
              employees: [...adminSearchedData.employee.employees, ...sortedSearchResults],
              pageInfo: searchResults?.pageInfo,
            },
          })
        }
        break

      case AdminSearchItemType.DEPARTMENT:
        {
          const searchResults = await api.admin.adminDepartment({
            query: search.query,
            apId,
            after: adminSearchedData?.department?.pageInfo?.endCursor,
          })
          const sortedSearchResults = searchResults?.edges.map((edge) => ({
            ...edge.node,
            type: type as AdminSearchItemType,
          })) as []

          setAdminSearchedData({
            ...adminSearchedData,
            department: {
              departments: [...adminSearchedData.department.departments, ...sortedSearchResults],
              pageInfo: searchResults?.pageInfo,
            },
          })
        }
        break

      case AdminSearchItemType.SPACE:
        {
          const searchResults = await api.admin.adminSpace({
            query: search.query,
            apId,
            after: adminSearchedData?.space?.pageInfo?.endCursor,
          })
          const sortedSearchResults = searchResults?.edges.map((edge) => ({
            ...edge.node,
            type: type as AdminSearchItemType,
          })) as []

          setAdminSearchedData({
            ...adminSearchedData,
            space: {
              spaces: [...adminSearchedData.space.spaces, ...sortedSearchResults],
              pageInfo: searchResults?.pageInfo,
            },
          })
        }
        break

      case AdminSearchItemType.NEIGHBORHOOD:
        {
          const searchResults = await api.admin.adminNeighborhood({
            query: search.query,
            apId,
            after: adminSearchedData?.neighborhood?.pageInfo?.endCursor,
          })
          const sortedSearchResults = searchResults?.edges.map((edge) => ({
            ...edge.node,
            type: type as AdminSearchItemType,
          })) as []

          setAdminSearchedData({
            ...adminSearchedData,
            neighborhood: {
              neighborhoods: [
                ...adminSearchedData.neighborhood.neighborhoods,
                ...sortedSearchResults,
              ],
              pageInfo: searchResults?.pageInfo,
            },
          })
        }
        break
    }
  }
  return (
    <Downshift
      onSelect={(
        selection: IAdminSearchResultItem | null,
        helpers: ControllerStateAndHelpers<IAdminSearchResultItem>
      ) => {
        if (search.loading) {
          helpers.openMenu()
          return
        }
        onSelect(selection)
      }}
      itemToString={(item) => (item ? item.name : '')}>
      {({
        openMenu,
        closeMenu,
        getInputProps,
        getItemProps,
        getMenuProps,
        getRootProps,
        isOpen,
        highlightedIndex,
      }) => (
        <div
          className="flex-auto"
          style={{
            position: 'relative',
            width: width ? width : 'auto',
          }}
          {...getRootProps()}>
          {/* eslint-disable @typescript-eslint/ban-ts-comment */}
          {/* @ts-ignore */}
          <InputGroup
            {...getInputProps({
              fill: true,
              value: search.query,
              onChange: ({ target: { value } }: any) => {
                closeRightDrawers()
                search.run(value)
              },
              className: 'new-border-color',
              'data-input': props['data-input'],
              placeholder: inputPlaceholder,
              leftElement: (
                <Flex
                  align="center"
                  justify="center"
                  style={{
                    width: 30,
                    height: 30,
                    color: 'var(--input-icon-color)',
                  }}>
                  {leftIcon}
                </Flex>
              ),
              rightElement: (
                <Flex
                  align="center"
                  justify="center"
                  style={{
                    width: 30,
                    height: 30,
                    color: 'var(--input-icon-color)',
                  }}>
                  <span
                    className={css.closeIcon}
                    onClick={() => {
                      search.run('')
                      closeMenu()
                    }}>
                    {rightIcon}
                  </span>
                </Flex>
              ),
              onFocus: ({ target }: { target: any }) => {
                setShowFilters(false)
                closeRightDrawers()
                if (search.query) {
                  openMenu()
                  search.reload && search.reload()
                  target.select()
                }

                if (hasRecentSearch) {
                  openMenu()
                }
              },
              onKeyDown: (event: any) => {
                if (
                  event.keyCode === KeyCodes.RETURN &&
                  search.query &&
                  highlightedIndex === null
                ) {
                  event.nativeEvent.preventDownshiftDefault = true
                  closeMenu()
                }
              },
              onClick: () => {
                setShowFilters(false)
                closeRightDrawers()
                openMenu()
              },
            })}
          />
          {isOpen && search?.query?.length > minimumQueryLength && (
            <div className="search__results admin-search" {...getMenuProps()}>
              <NoResults search={search} />
              {Boolean(adminSearchedData?.employee.employees.length && search.query) && (
                <React.Fragment>
                  <MenuSeparator
                    text={`EMPLOYEES (${adminSearchedData?.employee.employees.length})`}
                    paddingRight="5px"
                    containerStyle={{ lineHeight: '47px' }}
                  />

                  {adminSearchedData?.employee.employees?.map((item, index) => {
                    const active = index === highlightedIndex

                    return (
                      <DraggableEmployeeList
                        getItemProps={getItemProps}
                        renderItem={renderItem}
                        closeMenu={closeMenu}
                        item={item}
                        active={active}
                      />
                    )
                  })}
                  {adminSearchedData.employee.pageInfo?.hasNextPage && (
                    <ShowMore showMoreItems={showMoreItems} type={AdminSearchItemType.EMPLOYEE} />
                  )}
                </React.Fragment>
              )}
              {Boolean(adminSearchedData?.department.departments.length) && (
                <React.Fragment>
                  <MenuSeparator
                    text={`DEPARTMENTS (${adminSearchedData?.department.departments.length})`}
                    paddingRight="5px"
                    containerStyle={{ lineHeight: '47px' }}
                  />
                  {adminSearchedData?.department.departments.map((item, index) => {
                    const active = index === highlightedIndex

                    return (
                      <div
                        className={`search__result${active ? ' active' : ''} borderless`}
                        {...getItemProps({
                          item,
                          key: `search_result-${item.id}`,
                        })}>
                        {renderItem(item)}
                      </div>
                    )
                  })}
                  {adminSearchedData.department.pageInfo?.hasNextPage && (
                    <ShowMore showMoreItems={showMoreItems} type={AdminSearchItemType.DEPARTMENT} />
                  )}
                </React.Fragment>
              )}
              {Boolean(adminSearchedData.neighborhood.neighborhoods.length) && (
                <React.Fragment>
                  <MenuSeparator
                    text={`NEIGHBORHOODS (${adminSearchedData.neighborhood.neighborhoods.length})`}
                    paddingRight="5px"
                    containerStyle={{ lineHeight: '47px' }}
                  />
                  {adminSearchedData.neighborhood.neighborhoods.map((item, index) => {
                    const active = index === highlightedIndex

                    return (
                      <div
                        className={`search__result${active ? ' active' : ''} borderless`}
                        {...getItemProps({
                          item,
                          key: `search_result-${item.id}`,
                        })}>
                        {renderItem(item)}
                      </div>
                    )
                  })}
                  {adminSearchedData.neighborhood.pageInfo?.hasNextPage && (
                    <ShowMore
                      showMoreItems={showMoreItems}
                      type={AdminSearchItemType.NEIGHBORHOOD}
                    />
                  )}
                </React.Fragment>
              )}
              {Boolean(adminSearchedData.space.spaces.length) && (
                <React.Fragment>
                  <MenuSeparator
                    text={`SPACES (${adminSearchedData.space.spaces.length})`}
                    paddingRight="5px"
                    containerStyle={{ lineHeight: '47px' }}
                  />
                  {adminSearchedData.space.spaces.map((item, index) => {
                    const active = index === highlightedIndex

                    return (
                      <div
                        className={`search__result${active ? ' active' : ''} borderless`}
                        {...getItemProps({
                          item,
                          key: `search_result-${item.id}`,
                        })}>
                        {renderItem(item)}
                      </div>
                    )
                  })}
                  {adminSearchedData.space.pageInfo?.hasNextPage && (
                    <ShowMore showMoreItems={showMoreItems} type={AdminSearchItemType.SPACE} />
                  )}
                </React.Fragment>
              )}
            </div>
          )}
        </div>
      )}
    </Downshift>
  )
}

const ShowMore = ({
  showMoreItems,
  type,
}: {
  showMoreItems: (type: string) => void
  type: string
}) => {
  return (
    <div
      key={type}
      className={`search__result borderless' ${css.bntShowMore}`}
      onClick={(e) => {
        e.stopPropagation()
        showMoreItems(type)
      }}>
      SHOW MORE
    </div>
  )
}
const NoResults = ({
  search,
}: {
  search: UseAdminSearch
  queryTable?: (query: string) => void
}) => {
  const searchIsEmpty =
    search.items?.departments.edges.length ||
    search.items?.spaces.edges.length ||
    search.items?.neighborhoods.edges.length ||
    search.items?.employees.edges.length

  if (search.query.length < 1 || searchIsEmpty) return null

  return (
    <div>
      <div className={css.nodata}>
        <p>We couldn't find any matches.</p>
        <ul className={css.listType}>
          <li>Try a different spelling</li>
          <li>Enter more than 3 characters</li>
        </ul>
      </div>
    </div>
  )
}

const DraggableEmployeeList = ({
  getItemProps,
  renderItem,
  closeMenu,
  item,
  active,
}: {
  getItemProps: (options: GetItemPropsOptions<any>) => any
  renderItem: (item: IAdminSearchResultItem, helpers?: { closeMenu: () => void }) => void
  closeMenu: (cb?: () => void) => void
  item: any
  active: boolean
}) => {
  const [{ isDragging }, drag, preview] = useDrag({
    item: { type: 'Employee', data: item },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  })

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true })
    isDragging && closeMenu()
  }, [preview, closeMenu, isDragging])

  const { onClick, onMouseDown, onMouseMove, ...restProps } = getItemProps({
    item,
    key: `search_result-${item.id}`,
  })

  return (
    <div
      ref={drag}
      key={item?.id}
      className={`search__result${active ? ' active' : ''} min-h-80 borderless`}
      style={{ cursor: isDragging ? 'grabbing' : 'pointer' }}
      {...restProps}
      onClick={(e) => onClick(e)}>
      {renderItem(item, { closeMenu })}
    </div>
  )
}
